import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ClockIcon, InfoIcon } from "lucide-react";
import { useGetVideosQuery } from "@/features/auth/api/authAPI";
import styles from "./Header.module.scss";
import { useDispatch } from "react-redux";
import { useMe, useCurrentOrganization } from "@/Hooks";
import mixpanel from "@/shared/lib/mixpanel";
import { additionlInfoModalActions } from "@/entities/additionInfoModal";

interface VersionsMenuProps {
  appId: number;
}

const VersionsMenu: React.FC<VersionsMenuProps> = ({ appId }) => {
  const dispatch = useDispatch();
  const { me } = useMe();
  const currentOrganization = useCurrentOrganization();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleExpand = () => {
    mixpanel.track("Info clicked", {
      app_name: "App",
      idApp: appId,
      UserEmail: me?.email || "",
      OrganizationID: currentOrganization?.uuid || "",
    });
    dispatch(additionlInfoModalActions.toggleItem());
  };

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Get current query params
  const queryParams = new URLSearchParams(location.search);
  const currentVideoId = queryParams.get("av");

  // Fetch videos for this app
  const { data: videos, isLoading: isLoadingVideos } = useGetVideosQuery(
    { appId },
    { skip: !appId },
  );

  // Set selected version from URL or default to first video in the list
  const [selectedVideoId, setSelectedVideoId] = useState<number | null>(
    currentVideoId ? parseInt(currentVideoId) : null,
  );

  // Update selectedVideoId when videos are loaded or currentVideoId changes
  useEffect(() => {
    if (videos?.results?.length && !selectedVideoId) {
      setSelectedVideoId(videos.results[0].id);
    } else if (currentVideoId) {
      setSelectedVideoId(parseInt(currentVideoId));
    }
  }, [videos, currentVideoId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Get the selected video from the videos list
  const selectedVideo = videos?.results?.find(
    (video) => video.id === selectedVideoId,
  );

  const handleVersionSelect = (videoId: number) => {
    // Update URL with the selected video ID
    const newParams = new URLSearchParams(queryParams);
    newParams.set("av", videoId.toString());

    // Update URL without reloading the page
    navigate(
      {
        pathname: location.pathname,
        search: newParams.toString(),
      },
      { replace: true },
    );

    setSelectedVideoId(videoId);
    setIsDropdownOpen(false);
  };

  if (videos?.results && videos.results.length <= 1) {
    return null;
  }

  return (
    <div className={styles.versionsMenuContainer}>
      {videos?.results && videos.results.length > 0 && !isLoadingVideos && (
        <div
          ref={dropdownRef}
          className={styles.multipuleVersionContainer}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <ClockIcon size={18} />
          <p>{selectedVideo?.label}</p>
          <img src="/img/blackIcon/DropDown.svg" alt="arrowDown" />

          {isDropdownOpen && (
            <div className={styles.multipuleVersionDropdown}>
              {videos.results.map((video) => (
                <p
                  key={video.id}
                  className={`${styles.multipuleVersionDropdownItem} ${selectedVideoId === video.id ? styles.active : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleVersionSelect(video.id);
                  }}
                >
                  {video.label}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
      <button
        onClick={() => handleExpand()}
        className={styles.moreInfoButtonVersions}
      >
        <InfoIcon size={18} className={styles.infoIcon} />
        <p>More Info</p>
      </button>
    </div>
  );
};

export default VersionsMenu;
